/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import type { ProductGroup } from "./types";

/**
 * True when `product` appears in `options`. An empty product counts as present,
 * because "nothing selected" is always a valid state for the dropdown.
 *
 * Used by the forms that swap the product list when the issue type changes: a
 * product chosen under one issue type may not exist under the next one, and a
 * select whose value is missing from its options renders blank while the form
 * still holds — and would still submit — the old value.
 */
export function isProductInOptions(
  product: string,
  options: ProductGroup[],
): boolean {
  if (!product) return true;
  return options.some((group) => group.items.includes(product));
}

/*
 * IND-7: SUPPORT_EXCLUDED_PRODUCTS, REFUND_EXCLUDED_PRODUCTS and
 * MOBILE_APP_GROUP used to live here.
 *
 * The first two were hand-typed product names encoding two facts Django did
 * not publish — does this product issue a licence key, and is it free. Django
 * publishes both now, so the rules are derived from the catalogue and an admin
 * rename can no longer break them. See toUnlicensedProductNames and
 * toFreeProductNames in src/lib/productCatalogue/.
 *
 * MOBILE_APP_GROUP moved to the server, which validates what these forms
 * submit (IND-10). A copy here would drift from the copy that accepts or
 * refuses a submission. It arrives from support-catalogue/ instead.
 */

/** Maximum file size for uploads (10MB) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Allowed MIME types for file uploads (images only — matches backend ImageField) */
export const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

/** Duration to show success message before auto-hide (ms) */
export const SUCCESS_DISPLAY_MS = 5000;

/**
 * Legacy URL query parameter values (?product=X) mapped to product names.
 *
 * Kept deliberately: links using these values are already published in emails,
 * on the internet and inside the desktop products, and cannot be enumerated. A
 * live product_slug is tried first, so a new product needs no entry here (V-5).
 */
export const PRODUCT_QUERY_PARAM_MAP: Record<string, string> = {
  SecurityMonitorPro: "Security Monitor Pro",
  WebCamMonitor: "WebCam Monitor",
  IPCameraViewer: "IP Camera Viewer",
  MyScreenRecorder: "My Screen Recorder",
  MyScreenRecorderPro: "My Screen Recorder Pro",
  AutoFTPManager: "Auto FTP Manager",
  FTPManagerLite: "FTP Manager Lite",
  TextSpeakerLive: "Text Speaker Live",
  DictationPro: "Dictation Pro",
  DocumentTranslator: "Document Translator",
  IPCameraMonitor: "IP Camera Monitor",
  ScreenCast: "Screen Cast",
};
